import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { PORTAL_ENVIRONMENT_TOKEN, ValidPasswordValidator } from '@campus/public-portal';
import '@diekeure/dcr-components/dcr-filled-button.js';
import '@diekeure/dcr-components/dcr-inline-button.js';
import '@diekeure/dcr-components/dcr-text-field.js';
import { merge, take } from 'rxjs';

export interface RegisterTeacherInterface {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export const passwordMatchedValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const password = control.root.get('password');
  const passwordCheck = control.root.get('passwordCheck');

  return password && passwordCheck && password.value !== passwordCheck.value ? { noPasswordMatch: true } : null;
};

export const passwordNoUserInfoValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const password = control.value?.toLowerCase() || '';
  if (!password) {
    return null;
  }

  const attributes: string[] = [];
  const pushIfSet = (value: string | null | undefined) => {
    if (value) {
      attributes.push(value.toLowerCase());
    }
  };

  pushIfSet(control.root.get('firstName')?.value);
  pushIfSet(control.root.get('lastName')?.value);

  const email = control.root.get('email')?.value || '';
  if (email.includes('@')) {
    pushIfSet(email.split('@')[0]);
  }

  if (attributes.length === 0) {
    return null;
  }

  const containsUserInfo = attributes.some((attribute) => password.includes(attribute));

  return containsUserInfo ? { 'no-user-info': true } : null;
};

@Component({
  standalone: false,
  selector: 'polpo-register-teacher-form',
  templateUrl: './register-teacher-form.component.html',
})
export class RegisterTeacherFormComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private validPasswordValidator: ValidPasswordValidator = inject(ValidPasswordValidator);
  private title = inject(Title);

  public portalEnvironment = inject(PORTAL_ENVIRONMENT_TOKEN);

  @Input() error: string;
  @Output() register = new EventEmitter<RegisterTeacherInterface>();

  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.title.setTitle('Polpo - Registreren als leerkracht');

    // Revalidate password when firstName, lastName or email changes
    merge(
      this.registerForm.get('firstName').valueChanges,
      this.registerForm.get('lastName').valueChanges,
      this.registerForm.get('email').valueChanges
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.registerForm.get('password').updateValueAndValidity();
      });
  }

  public registerForm = this.formBuilder.group({
    firstName: this.formBuilder.control('', {
      validators: [Validators.required],
    }),
    lastName: this.formBuilder.control('', {
      validators: [Validators.required],
    }),
    email: this.formBuilder.control('', {
      validators: [Validators.required, Validators.email],
    }),
    password: this.formBuilder.control('', {
      validators: [
        Validators.required,
        Validators.minLength(12),
        Validators.maxLength(128),
        passwordNoUserInfoValidator,
      ],
      asyncValidators: [this.validPasswordValidatorFn()],
    }),
    passwordCheck: new FormControl('', {
      validators: [Validators.required, passwordMatchedValidator],
    }),
  });

  private validPasswordValidatorFn() {
    return (control: AbstractControl) => this.validPasswordValidator.validate(control).pipe(take(1));
  }

  public getControl(name: string): FormControl {
    return this.registerForm.get(name) as FormControl;
  }

  public onSubmit() {
    if (!this.registerForm.valid) {
      this.registerForm.markAllAsTouched();
      return;
    }

    this.register.emit(this.registerForm.value as RegisterTeacherInterface);
  }
}
